import React, { useState, useEffect } from 'react';
import {
  FileText,
  Search,
  Calendar,
  CheckCircle2,
  Clock,
  FileDown,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Building2,
  DollarSign,
  ShieldCheck,
  AlertTriangle,
  ShieldAlert,
  AlertCircle
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { areaClienteService } from '../../services/areaClienteService';
import { generatePropostaPdf } from '../../services/propostaPdfGenerator';
import { propostaSaldoService, PropostaSaldoInfo } from '../../services/propostaSaldoService';
import { fixEncodingIssues } from '../../utils/textUtils';

export function AreaClientePropostas() {
  const { clienteAtual } = useAreaCliente();
  const [propostas, setPropostas] = useState<any[]>([]);
  const [saldosMap, setSaldosMap] = useState<Map<number, PropostaSaldoInfo>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [generatingPdfId, setGeneratingPdfId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadPropostas = async () => {
    if (!clienteAtual) return;
    setIsLoading(true);
    try {
      const data = await areaClienteService.getPropostasCliente(clienteAtual.id);
      setPropostas(data || []);

      // Carrega informações de saldos e cotas para alertas amigáveis ao cliente
      try {
        const saldos = await propostaSaldoService.getBatchSaldos(data || []);
        setSaldosMap(saldos);
      } catch (errSaldo) {
        console.warn('Erro ao carregar saldos das propostas para o cliente:', errSaldo);
      }
    } catch (err) {
      console.error('Erro ao carregar propostas do cliente:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPropostas();
  }, [clienteAtual]);

  const toggleExpand = (id: number) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleDownloadPdf = async (proposta: any) => {
    setGeneratingPdfId(proposta.id);
    try {
      await generatePropostaPdf(proposta);
    } catch (err) {
      console.error('Erro ao gerar PDF da proposta:', err);
    } finally {
      setGeneratingPdfId(null);
    }
  };

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  const filteredPropostas = propostas.filter(p => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const matchId = String(p.id).includes(term);
    const matchStatus = (p.status || '').toLowerCase().includes(term);
    const matchConsideracoes = (p.consideracoes || '').toLowerCase().includes(term);
    return matchId || matchStatus || matchConsideracoes;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900">Propostas e Contratos</h1>
            <p className="text-xs text-slate-500">
              Consulte suas propostas comerciais, escopos vigentes, condições contratuais e tabelas de frete.
            </p>
          </div>
        </div>

        <button
          onClick={loadPropostas}
          className="self-start sm:self-auto px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por número da proposta, condições ou status..."
            className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* List of Proposals */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="py-16 text-center bg-white rounded-2xl border border-slate-200">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-3 text-xs text-slate-500">Carregando propostas e contratos...</p>
          </div>
        ) : filteredPropostas.length === 0 ? (
          <div className="py-16 text-center space-y-3 bg-white rounded-2xl border border-slate-200 p-6">
            <FileText className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">Nenhuma proposta encontrada</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Nenhuma proposta ou contrato comercial registrado para esta conta no momento.
            </p>
          </div>
        ) : (
          filteredPropostas.map((prop) => {
            const isExpanded = expandedIds.includes(prop.id);
            const isApproved = (prop.status || '').toLowerCase().includes('aprovad');
            const items = prop.detalhes_do_pedido || [];
            const saldoInfo = saldosMap.get(prop.id);

            return (
              <div
                key={prop.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all hover:border-purple-200"
              >
                {/* Proposal Header Card */}
                <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5">
                      <span className="text-base font-black text-slate-900">
                        Proposta Comercial #{prop.id}
                      </span>
                      <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${
                        isApproved
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : 'bg-amber-100 text-amber-800 border-amber-300'
                      }`}>
                        {prop.status || 'Vigente'}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        Emitida em: {prop.created_at ? new Date(prop.created_at).toLocaleDateString('pt-BR') : '-'}
                      </span>
                      <span>•</span>
                      <span>{items.length} item(ns) contratado(s)</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 self-end sm:self-center">
                    <button
                      onClick={() => handleDownloadPdf(prop)}
                      disabled={generatingPdfId === prop.id}
                      className="inline-flex items-center space-x-1.5 px-3.5 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold text-xs rounded-xl transition-colors disabled:opacity-50"
                    >
                      {generatingPdfId === prop.id ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-purple-700 border-t-transparent rounded-full animate-spin"></div>
                          <span>Gerando PDF...</span>
                        </>
                      ) : (
                        <>
                          <FileDown className="w-4 h-4" />
                          <span>Baixar Proposta PDF</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => toggleExpand(prop.id)}
                      className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors"
                      title={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Banner de Aviso ao Cliente sobre o Saldo do Contrato */}
                {saldoInfo && saldoInfo.totalContratado > 0 && saldoInfo.nivelAlertaGeral !== 'normal' && saldoInfo.nivelAlertaGeral !== 'sem_cota' && (
                  <div className="px-5 sm:px-6 pb-4">
                    {saldoInfo.nivelAlertaGeral === 'esgotado_0' ? (
                      <div className="p-3 bg-red-50 border border-red-300 rounded-xl flex items-start gap-2.5 text-xs text-red-900 shadow-xs">
                        <ShieldAlert className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                        <div>
                          <strong className="font-bold uppercase tracking-wider text-[11px] block">
                            ⛔ Cota Totalmente Consumida: Emissão de Fretes Bloqueada
                          </strong>
                          <span>
                            Todas as {saldoInfo.totalContratado} viagens/serviços contratados nesta proposta comercial foram realizados (Saldo: 0). Novas solicitações de frete vinculadas a este contrato estão bloqueadas. Entre em contato para contratação de aditivo ou renovação comercial.
                          </span>
                        </div>
                      </div>
                    ) : saldoInfo.nivelAlertaGeral === 'urgente_1' ? (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5 text-xs text-red-900 shadow-xs">
                        <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                        <div>
                          <strong className="font-bold uppercase tracking-wider text-[11px] block">
                            ⚠️ Aviso Importante: Resta Apenas 1 Viagem Disponível
                          </strong>
                          <span>
                            Atenção! Resta apenas 1 serviço/frete disponível no saldo desta proposta comercial. Providencie a renovação do seu pacote de fretes para manter suas operações contínuas.
                          </span>
                        </div>
                      </div>
                    ) : saldoInfo.nivelAlertaGeral === 'critico_5' ? (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-2.5 text-xs text-orange-900 shadow-xs">
                        <AlertCircle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                        <div>
                          <strong className="font-bold uppercase tracking-wider text-[11px] block">
                            🚨 Alerta Crítico: Cota Quase Esgotada ({saldoInfo.saldoRestante} fretes restantes)
                          </strong>
                          <span>
                            {saldoInfo.alertaMensagemGeral} Recomendamos planejar a renovação contratual antes do consumo total da cota disponível.
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 shadow-xs">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <strong className="font-bold uppercase tracking-wider text-[11px] block">
                            ⚠️ Aviso de Saldo Baixo ({saldoInfo.saldoRestante} fretes restantes)
                          </strong>
                          <span>
                            {saldoInfo.alertaMensagemGeral} Acompanhe o consumo da sua cota comercial para evitar paralisações no atendimento.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50/70 p-5 sm:p-6 space-y-6">
                    
                    {/* Itemized Services Table (Sem coluna de saldo para cliente, apenas escopo e tarifas) */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                        Tabela de Serviços e Tarifas Contratadas
                      </h4>
                      {items.length === 0 ? (
                        <p className="text-xs text-slate-400">Nenhum item discriminado nesta proposta.</p>
                      ) : (
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-100 text-slate-500 font-bold uppercase text-[10px]">
                              <tr>
                                <th className="py-2.5 px-3">Serviço / Descrição</th>
                                <th className="py-2.5 px-3 text-center">Quantidade</th>
                                <th className="py-2.5 px-3 text-right">Valor Unitário</th>
                                <th className="py-2.5 px-3 text-right">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {items.map((it: any) => (
                                <tr key={it.id} className="hover:bg-slate-50">
                                  <td className="py-2.5 px-3 font-semibold text-slate-800">{fixEncodingIssues(it.servicos) || 'Serviço de Transporte'}</td>
                                  <td className="py-2.5 px-3 text-center text-slate-600">{it.quantidade || 1}</td>
                                  <td className="py-2.5 px-3 text-right text-slate-600">{formatCurrency(it.valor_unitario)}</td>
                                  <td className="py-2.5 px-3 text-right font-bold text-slate-900">{formatCurrency(it.valor_total)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Terms & Conditions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      {prop.condicoes_comerciais && (
                        <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-1">
                          <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">Condições Comerciais</span>
                          <p className="text-slate-600 leading-relaxed">{fixEncodingIssues(prop.condicoes_comerciais)}</p>
                        </div>
                      )}

                      {prop.condicoes_pagamento && (
                        <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-1">
                          <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">Condições de Pagamento</span>
                          <p className="text-slate-600 leading-relaxed">{fixEncodingIssues(prop.condicoes_pagamento)}</p>
                        </div>
                      )}

                      {prop.consideracoes && (
                        <div className="md:col-span-2 bg-white p-4 rounded-xl border border-slate-200 space-y-1">
                          <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">Considerações Gerais</span>
                          <p className="text-slate-600 leading-relaxed">{fixEncodingIssues(prop.consideracoes)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
